import type { Direction, GridPosition } from "../models/ModuleGrid.js";
import { shapeIdForPorts } from "./moduleShapes.js";

/**
 * 5×5 그리드 (0,2)→(4,2) 해밀턴 경로 — Start 진입칸(0,2)에서 시작해 25칸을 한 붓으로 전부 지나
 * Fire 진출칸(4,2)에서 끝난다. 각 칸이 경로상 정확히 2개의 이웃을 가져 직선/코너 실제 모양으로 채워지고,
 * 물이 모든 칸을 통과하므로 배치 모듈 전부가 발동한다(cross 통일과 달리 실제 다양한 모양 유지).
 *
 * 인접성: 연속 좌표는 항상 상하좌우 1칸 차이. (0,2)는 Start(-1,2)와 left로, (4,2)는 Fire(5,2)와 right로 맞물린다.
 */
const HAMILTONIAN_PATH_5X5: readonly GridPosition[] = [
  { x: 0, y: 2 }, { x: 0, y: 1 }, { x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 },
  { x: 1, y: 2 }, { x: 1, y: 3 }, { x: 0, y: 3 }, { x: 0, y: 4 }, { x: 1, y: 4 },
  { x: 2, y: 4 }, { x: 2, y: 3 }, { x: 2, y: 2 }, { x: 2, y: 1 }, { x: 2, y: 0 },
  { x: 3, y: 0 }, { x: 4, y: 0 }, { x: 4, y: 1 }, { x: 3, y: 1 }, { x: 3, y: 2 },
  { x: 3, y: 3 }, { x: 3, y: 4 }, { x: 4, y: 4 }, { x: 4, y: 3 }, { x: 4, y: 2 },
];

/** from→to 단위 이동 방향 (인접 셀 전제). 그리드 y는 화면 아래로 증가. */
export function directionBetween(from: GridPosition, to: GridPosition): Direction {
  if (to.x > from.x) return "right";
  if (to.x < from.x) return "left";
  if (to.y > from.y) return "down";
  return "up";
}

export interface FillCell {
  readonly x: number;
  readonly y: number;
  /** 경로상 이전·다음 이웃 방향 2개로 도출한 실제 연결 모양(직선/코너). */
  readonly shapeId: string;
}

interface GridShape {
  readonly width: number;
  readonly height: number;
  readonly start: GridPosition;
  readonly fire: GridPosition;
}

/**
 * Start→Fire를 연결하는 사행 경로로 그리드를 채울 (셀, 실제 모양) 목록. 각 칸 모양은 경로상 이전·다음
 * 이웃 방향 2개로 결정(직선이면 straight, 꺾이면 corner). 첫 칸은 Start와, 마지막 칸은 Fire와 맞물린다.
 * 표준 5×5(start/fire가 y=2의 좌·우 endpoint)만 지원하며, 그 외 구성은 빈 배열을 반환한다.
 */
export function connectedFillCells(grid: GridShape): FillCell[] {
  if (grid.width !== 5 || grid.height !== 5 || grid.start.y !== 2 || grid.fire.y !== 2) return [];
  const nodes: GridPosition[] = [grid.start, ...HAMILTONIAN_PATH_5X5, grid.fire];
  const cells: FillCell[] = [];
  for (let i = 1; i < nodes.length - 1; i++) {
    const node = nodes[i]!;
    const prevDir = directionBetween(node, nodes[i - 1]!);
    const nextDir = directionBetween(node, nodes[i + 1]!);
    cells.push({ x: node.x, y: node.y, shapeId: shapeIdForPorts([prevDir, nextDir]) });
  }
  return cells;
}
